/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Current customer-facing state of a home’s Buy Direct purchase option. */
export type MarketplaceBuyDirectStatus =
  /** The Buy Direct option is available for a new offer. */
  | 'AVAILABLE'
  /** The home has another offer and is accepting backup offers. */
  | 'BACKUP_OFFER'
  /** The Buy Direct option is already under contract. */
  | 'IN_CONTRACT'
  /** The home is accepting offers until a deadline. */
  | 'OFFER_DEADLINE';

/** Customer-facing badge describing a marketplace home’s availability. */
export type MarketplaceDisplayBadge =
  /** The home is expected to become available soon. */
  | 'AVAILABLE_SOON'
  /** The home is under contract. */
  | 'IN_CONTRACT'
  /** The home is on the market and eligible for self-tour. */
  | 'ON_THE_MARKET'
  /** The home recently sold. */
  | 'RECENTLY_SOLD'
  /** The home is on the market but cannot currently be accessed for self-tour. */
  | 'TEMPORARILY_UNAVAILABLE'
  /** The home is on the market but does not support self-tour. */
  | 'TRADITIONAL'
  /** The home’s display status is unavailable. */
  | 'UNKNOWN';

/** Home type used to classify a marketplace listing. */
export type MarketplaceDwellingType =
  /** A condominium home. */
  | 'CONDO'
  /** A detached or single-family house. */
  | 'HOUSE'
  /** A property containing multiple residential units. */
  | 'MULTI_FAMILY'
  /** A dwelling type that does not fit the listed categories. */
  | 'OTHER'
  /** A townhouse. */
  | 'TOWNHOUSE';

/** Primary kitchen countertop material reported for a home. */
export type MarketplaceKitchenCountertop =
  /** Corian kitchen countertop surface. */
  | 'CORIAN'
  /** Formica kitchen countertop surface. */
  | 'FORMICA'
  /** Granite slab kitchen countertop surface. */
  | 'GRANITE_SLAB'
  /** Granite tile kitchen countertop surface. */
  | 'GRANITE_TILE'
  /** Tile kitchen countertop surface other than granite. */
  | 'OTHER_TILE'
  /** Quartz kitchen countertop surface. */
  | 'QUARTZ'
  /** The kitchen countertop material could not be determined. */
  | 'UNKNOWN';

/** Status recorded for a historical marketplace listing event. */
export type MarketplaceListingHistoryState =
  /** The home was announced as coming soon. */
  | 'COMING_SOON'
  /** The home was offered for sale. */
  | 'FOR_SALE'
  /** The home was not actively offered for sale. */
  | 'OFF_MARKET'
  /** The home had a pending sale. */
  | 'SALE_PENDING'
  /** The home was sold. */
  | 'SOLD'
  /** The historical listing status could not be determined. */
  | 'UNKNOWN'
  /** The home was withdrawn from the market. */
  | 'WITHDRAWN';

/** Current customer-facing status of a marketplace listing. */
export type MarketplaceListingState =
  /** The home is expected to become available for purchase soon. */
  | 'AVAILABLE_SOON'
  /** The home is under contract with a buyer. */
  | 'IN_CONTRACT'
  /** The home is currently offered for purchase. */
  | 'ON_THE_MARKET'
  /** The home has recently sold. */
  | 'RECENTLY_SOLD'
  /** The home’s current marketplace status could not be determined. */
  | 'UNKNOWN';

/** Floor where the primary bedroom is located. */
export type MarketplacePrimaryBedroomLocation =
  /** The primary bedroom is in the basement. */
  | 'BASEMENT'
  /** The primary bedroom is on the first floor. */
  | 'FIRST_FLOOR'
  /** The primary bedroom is on the second floor. */
  | 'SECOND_FLOOR'
  /** The primary bedroom is on the third floor. */
  | 'THIRD_FLOOR'
  /** The primary bedroom location could not be determined. */
  | 'UNKNOWN';

export type GetMarketplacePropertyDetailsQueryVariables = Exact<{
  addressId: Types.Scalars['UUID']['input'];
  includeSimilarListings: Types.Scalars['Boolean']['input'];
}>;


export type GetMarketplacePropertyDetailsQuery = { marketplace: { listingDetails: { annualPropertyTaxCents: Types.Scalars['SafeInt']['output'] | null, tourBookingListingId: Types.Scalars['NonEmptyString']['output'] | null, daysOnMarket: Types.Scalars['SafeInt']['output'] | null, description: Types.Scalars['String']['output'] | null, exteriorStories: Types.Scalars['SafeInt']['output'] | null, hoaMonthlyDuesCents: Types.Scalars['SafeInt']['output'] | null, garageSpaces: Types.Scalars['SafeInt']['output'] | null, hasSellerDisclosures: Types.Scalars['Boolean']['output'] | null, kitchenCountertop: Types.MarketplaceKitchenCountertop | null, primaryBedroomLocation: Types.MarketplacePrimaryBedroomLocation | null, schoolDistricts: Array<Types.Scalars['NonEmptyString']['output']> | null, shouldShowGuarantee: Types.Scalars['Boolean']['output'], unitNumber: Types.Scalars['String']['output'] | null, unitType: Types.Scalars['String']['output'] | null, availability: { canSelfTour: Types.Scalars['Boolean']['output'] | null, displayBadge: Types.MarketplaceDisplayBadge, state: Types.MarketplaceListingState } | null, buyDirect: { priceCents: Types.Scalars['SafeInt']['output'], savingsCents: Types.Scalars['SafeInt']['output'] | null, status: Types.MarketplaceBuyDirectStatus } | null, floorPlanPhotos: Array<{ label: Types.Scalars['NonEmptyString']['output'], url: Types.Scalars['URL']['output'] }>, listingHistory: Array<{ currentListPriceCents: Types.Scalars['SafeInt']['output'] | null, mls: Types.Scalars['String']['output'] | null, originalListPriceCents: Types.Scalars['SafeInt']['output'] | null, soldPriceCents: Types.Scalars['SafeInt']['output'] | null, state: Types.MarketplaceListingHistoryState, timestamp: Types.Scalars['DateTime']['output'] | null }> | null, schools: Array<{ id: Types.Scalars['ID']['output'], name: Types.Scalars['NonEmptyString']['output'], rating: Types.Scalars['SafeInt']['output'] | null }> | null, similarListings?: Array<{ addressId: Types.Scalars['UUID']['output'], bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, canSelfTour: Types.Scalars['Boolean']['output'] | null, city: Types.Scalars['String']['output'] | null, dwellingType: Types.MarketplaceDwellingType, fullAddress: Types.Scalars['String']['output'] | null, listPriceCents: Types.Scalars['SafeInt']['output'] | null, neighborhood: Types.Scalars['String']['output'] | null, photoUrls: Array<Types.Scalars['URL']['output']>, slug: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null }> | null, photos: Array<{ extraLargeUrl: Types.Scalars['URL']['output'], extraSmallUrl: Types.Scalars['URL']['output'], largeUrl: Types.Scalars['URL']['output'], mediumUrl: Types.Scalars['URL']['output'], smallUrl: Types.Scalars['URL']['output'] }>, walkthroughVideos: Array<{ id: Types.Scalars['ID']['output'], url: Types.Scalars['URL']['output'] }> | null, summary: { addressId: Types.Scalars['UUID']['output'], bathrooms: Types.Scalars['Float']['output'] | null, bedrooms: Types.Scalars['SafeInt']['output'] | null, canSelfTour: Types.Scalars['Boolean']['output'] | null, city: Types.Scalars['String']['output'] | null, displayBadge: Types.MarketplaceDisplayBadge, dwellingType: Types.MarketplaceDwellingType, estimatedMonthlyPaymentCents: Types.Scalars['SafeInt']['output'] | null, latitude: Types.Scalars['Latitude']['output'] | null, listPriceCents: Types.Scalars['SafeInt']['output'] | null, listingState: Types.MarketplaceListingState, longitude: Types.Scalars['Longitude']['output'] | null, lotSquareFootage: Types.Scalars['SafeInt']['output'] | null, postalCode: Types.Scalars['PostalCode']['output'] | null, pricePerSquareFootCents: Types.Scalars['SafeInt']['output'] | null, slug: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, street: Types.Scalars['String']['output'] | null, totalLivingSquareFootage: Types.Scalars['SafeInt']['output'] | null, yearBuilt: Types.Scalars['SafeInt']['output'] | null } } | null } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const GetMarketplacePropertyDetailsDocument = /*#__PURE__*/ new TypedDocumentString(`
    query GetMarketplacePropertyDetails($addressId: UUID!, $includeSimilarListings: Boolean!) {
  marketplace {
    listingDetails(addressId: $addressId) {
      annualPropertyTaxCents
      tourBookingListingId
      availability {
        canSelfTour
        displayBadge
        state
      }
      buyDirect {
        priceCents
        savingsCents
        status
      }
      daysOnMarket
      description
      exteriorStories
      hoaMonthlyDuesCents
      floorPlanPhotos {
        label
        url
      }
      garageSpaces
      hasSellerDisclosures
      kitchenCountertop
      listingHistory {
        currentListPriceCents
        mls
        originalListPriceCents
        soldPriceCents
        state
        timestamp
      }
      primaryBedroomLocation
      schoolDistricts
      schools {
        id
        name
        rating
      }
      shouldShowGuarantee
      similarListings @include(if: $includeSimilarListings) {
        addressId
        bathrooms
        bedrooms
        canSelfTour
        city
        dwellingType
        fullAddress
        listPriceCents
        neighborhood
        photoUrls(first: 1)
        slug
        totalLivingSquareFootage
      }
      photos {
        extraLargeUrl
        extraSmallUrl
        largeUrl
        mediumUrl
        smallUrl
      }
      walkthroughVideos {
        id
        url
      }
      summary {
        addressId
        bathrooms
        bedrooms
        canSelfTour
        city
        displayBadge
        dwellingType
        estimatedMonthlyPaymentCents
        latitude
        listPriceCents
        listingState
        longitude
        lotSquareFootage
        postalCode
        pricePerSquareFootCents
        slug
        state
        street
        totalLivingSquareFootage
        yearBuilt
      }
      unitNumber
      unitType
    }
  }
}
    `) as unknown as TypedDocumentString<GetMarketplacePropertyDetailsQuery, GetMarketplacePropertyDetailsQueryVariables>;